import { Alert, Col, Descriptions, Divider, Flex, Row } from 'antd';
import { DataList } from '@/components/ui/list/DataList';
import { Button, Card, Modal, Tabs } from 'pink-lava-ui';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useMemo, useCallback, type ReactNode, useEffect, useState } from 'react';
import { BoxIcon, CheckCircle2, CheckCircleIcon, Clock10Icon } from 'lucide-react';
import type { BookingDraft, BookingDraftCount, User, Vehicle, ShippingWindow } from '@dc-trans/data-layer';
import { useBookingOnDemand, useBookingOnDemandCount } from '@/features/on-demand/api/queries';
import OnDemandCard from '@/components/ui/OnDemandCard';
import dayjs from 'dayjs';
import { FormModal } from '@/components/ui/modal';
import { bookingDraftConfirmSchema } from '@dc-trans/data-layer';
import { usePostBookingDraftConfirm } from '@/features/on-demand/api/mutations';
import { useNotification } from '@/context/notificationContext';
import queryClient from '@/libs/queryClient';
import type { FilterFieldType } from '@/components/ui/form/FilterBuilder';
import type { FieldValues } from 'react-hook-form';

const OnDemandAssignment = () => {
  const { filters, updateUrlParams } = useUrlParamsContext();
  const showNotification = useNotification();
  const [isModalConfirmationVisible, setIsModalConfirmationVisible] = useState(false);

  const { data: bookingDraftCount } = useBookingOnDemandCount();

  // Form modal state
  const [selectedBooking, setSelectedBooking] = useState<BookingDraft | undefined>();

  const { mutateAsync: confirmBookingDraft, isPending } = usePostBookingDraftConfirm();

  // Use filters.status directly as activeTab to avoid circular dependency
  const activeTab =
    filters.status?.includes('completed') || filters.status?.includes('waiting_shipment')
      ? 'completed,waiting_shipment'
      : 'new';

  // Handle tab changes by updating URL parameter
  const handleTabChange = useCallback(
    (newTab: string) => {
      if (newTab) {
        updateUrlParams({
          status: newTab,
          page: 1,
        });
      }
    },
    [updateUrlParams]
  );

  useEffect(() => {
    if (!filters.status) {
      updateUrlParams({ status: 'new', page: 1 });
    }
  }, [filters.status, updateUrlParams]);

  const mapOnDemandCount: {
    // eslint-disable-next-line no-unused-vars
    [K in keyof BookingDraftCount]: { label: string; value: number | undefined; icon: ReactNode };
  } = useMemo(() => {
    return {
      new: {
        label: 'Baru',
        value: bookingDraftCount?.data?.new,
        icon: <BoxIcon size={20} />,
      },
      waiting_shipment: {
        label: 'Menunggu Shipment',
        value: bookingDraftCount?.data?.waiting_shipment,
        icon: <Clock10Icon size={20} />,
      },
      completed: {
        label: 'Sudah Dikonfirmasi',
        value: bookingDraftCount?.data?.completed,
        icon: <CheckCircleIcon size={20} />,
      },
    };
  }, [bookingDraftCount]);

  const handleConfirmClick = (booking: BookingDraft) => {
    setSelectedBooking(booking);
  };

  const handleFormSubmit = async (data: any) => {
    await confirmBookingDraft(
      {
        id: selectedBooking?.ulid!,
        payload: {
          ...data,
          delivery_date: data.delivery_date ? dayjs(data.delivery_date).format('YYYY-MM-DD') : '',
        },
      },
      {
        onSuccess: () => {
          showNotification.success({
            message: 'Booking berhasil dikonfirmasi',
          });
          setSelectedBooking(undefined);
          setIsModalConfirmationVisible(true);
          queryClient.invalidateQueries({ queryKey: ['booking-on-demand-assignment'] });
          queryClient.invalidateQueries({ queryKey: ['booking-on-demand'] });
        },
      }
    );
  };

  const handleCancel = () => {
    setSelectedBooking(undefined);
  };

  return (
    <DataList
      title="Penugasan On Demand"
      filterFields={[
        {
          type: 'search',
          name: 'search',
          label: 'Cari Nomor Booking',
          placeholder: 'Cari Nomor Booking',
          trigger: 'onChange',
        },
        {
          type: 'search',
          name: 'origin_name',
          label: 'Asal Pengiriman',
          placeholder: 'Asal Pengiriman',
          trigger: 'onChange',
        },
        {
          type: 'search',
          name: 'dest_name',
          label: 'Tujuan Pengiriman',
          placeholder: 'Tujuan Pengiriman',
          trigger: 'onChange',
        },
        {
          label: 'Req. Plant Delivery Date',
          type: 'date-range',
          name: 'req_plant_delivery_date',
          placeholder: 'Tanggal awal,Tanggal akhir',
          rounded: 'large',
        },
        ...(activeTab === 'completed,waiting_shipment'
          ? [
              {
                type: 'select',
                name: 'status',
                label: 'Status',
                allowClear: false,
                placeholder: 'Status',
                rounded: 'large',
                options: [
                  { label: 'Semua', value: 'completed,waiting_shipment' },
                  { label: 'Menunggu Shipment', value: 'waiting_shipment' },
                  { label: 'Konfirmasi Berhasil', value: 'completed' },
                ],
              } as FilterFieldType<FieldValues>,
            ]
          : []),
      ]}
      defaultSort={{
        sortBy: 'created_at',
        sortOrder: 'desc',
      }}
    >
      <Flex gap={16} className="w-full h-full" vertical>
        <Flex className="w-full flex-1 overflow-y-scroll" gap={16} vertical>
          <DataList.Header
            hideFilterLabels={['status']}
            colProps={{
              xs: 24,
              sm: 24,
              md: 12,
              lg: 8,
              xl: 8,
              xxl: 6,
            }}
          >
            <Row gutter={[8, 8]} className="mt-2">
              {Object.entries(mapOnDemandCount).map(([key, data]) => (
                <Col xs={12} md={8} key={key}>
                  <div className="border-gray-200 border-[1px] shadow-sm rounded-2xl px-3 py-1.5 items-center flex flex-row gap-2">
                    {data.icon}
                    <div className="flex text-sm font-medium flex-1 h-[40px] items-center">{data.label}</div>
                    <div className="text-xl font-bold">{typeof data.value === 'number' ? data.value : '-'}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </DataList.Header>
          <Card
            rounded="large"
            classNames={{
              body: 'p-0! px-4!',
            }}
            className="mx-4!"
          >
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={[
                {
                  key: 'new',
                  label: 'Baru',
                },
                {
                  key: 'completed,waiting_shipment',
                  label: 'Sudah Dikonfirmasi',
                },
              ]}
            />
          </Card>
          {activeTab === 'completed,waiting_shipment' && (
            <div className="px-4">
              <Alert
                showIcon
                type="warning"
                className="text-amber-400!"
                message={`Status booking akan otomatis diperbarui setiap ${bookingDraftCount?.data?.cron_sync_booking_minutes} menit`}
              />
            </div>
          )}
          <DataList.List<BookingDraft>
            useQuery={useBookingOnDemand}
            queryKey={['booking-on-demand-assignment']}
            renderItem={booking => (
              <OnDemandCard key={booking.ulid} booking={booking} onConfirmClick={() => handleConfirmClick(booking)} />
            )}
          />
        </Flex>
        <FormModal
          visible={!!selectedBooking}
          mode="create"
          loading={isPending}
          title={'Konfirmasi Booking'}
          onCancel={handleCancel}
          onSubmit={handleFormSubmit}
          validationSchema={bookingDraftConfirmSchema}
          fields={[
            {
              name: 'driver_ulid',
              type: 'select',
              apiUrl: 'drivers',
              searchKey: 'search',
              apiParams: {
                page: 1,
                limit: 100,
                status: 'active',
              },
              placeholder: 'Pilih Pengemudi',
              required: true,
              renderOption: item => {
                const driver = item as User;
                return {
                  label: `${driver.name} - ${driver.username}`,
                  value: driver.ulid!,
                };
              },
            },
            {
              name: 'vehicle_ulid',
              type: 'select',
              apiUrl: 'vehicles',
              searchKey: 'search',
              apiParams: {
                page: 1,
                limit: 100,
                status: 'active',
              },
              placeholder: 'Pilih Nomor & Tipe Kendaraan',
              required: true,
              renderOption: item => {
                const vehicle = item as Vehicle;
                return {
                  label: `${vehicle.license_plate} - ${vehicle.vehicle_type?.name}`,
                  value: vehicle.ulid!,
                };
              },
            },
            {
              name: 'delivery_date',
              type: 'date',
              onChange: (value: any, setValue: any) => {
                setValue(value ? dayjs(value).format('YYYY-MM-DD') : '');
              },
              placeholder: 'Tanggal Pengambilan Barang',
              required: true,
            },
            {
              name: 'shipping_window_ulid',
              type: 'select',
              placeholder: 'Shipping Window',
              searchKey: 'name',
              apiUrl: 'shipping-windows',
              apiParams: {
                page: 1,
                limit: 100,
              },
              required: true,
              renderOption: item => {
                const shippingWindow = item as ShippingWindow;
                return {
                  label: `${shippingWindow.name} | ${shippingWindow.incoming} - ${shippingWindow.outgoing}`,
                  value: shippingWindow.ulid,
                };
              },
            },
          ]}
          submitButtonText={{
            create: 'Konfirmasi',
          }}
          header={
            <>
              <Descriptions
                column={1}
                colon={false}
                layout="vertical"
                size="small"
                classNames={{
                  label: 'font-nunito',
                  content: 'font-nunito font-semibold',
                }}
                items={[
                  {
                    label: 'Req. Plant Delivery Date',
                    children: selectedBooking?.req_plant_delivery_date
                      ? dayjs(selectedBooking?.req_plant_delivery_date).format('DD-MM-YYYY')
                      : '-',
                  },
                  {
                    label: 'Req. Jenis Kendaraan',
                    children: selectedBooking?.req_vehicle_type ? selectedBooking.req_vehicle_type.name : '-',
                  },
                  {
                    label: 'Req. Shipping Window',
                    children: selectedBooking?.req_ship_window
                      ? `${selectedBooking.req_ship_window.incoming} - ${selectedBooking.req_ship_window.outgoing}`
                      : '-',
                  },
                ]}
              />
              <Divider className="mt-4! mb-1!" />
            </>
          }
        />
        <Modal
          title={''}
          open={!!isModalConfirmationVisible}
          centered
          width={360}
          closable={false}
          onCancel={() => setIsModalConfirmationVisible(false)}
          footer={
            <Button
              shape="round"
              block
              onClick={() => {
                setIsModalConfirmationVisible(false);
                updateUrlParams({
                  status: 'completed,waiting_shipment',
                  page: 1,
                });
              }}
            >
              Lihat Tab Sudah Dikonfirmasi
            </Button>
          }
        >
          <div className="w-full flex flex-col items-center mt-8 gap-4">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle2 size={64} className="text-green-500" />
            </div>
            <div className="text-center text-gray-500">
              Booking Anda telah dikonfirmasi & sedang diproses, silahkan klik tombol di bawah ini untuk melihat
              booking.
            </div>
          </div>
        </Modal>
      </Flex>
    </DataList>
  );
};

export default OnDemandAssignment;
